import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, matchPath } from 'react-router-dom';
import { Provider } from 'react-redux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { store } from './store';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LoginPage, DashboardPage, AppLayout } from './App.core';
import { ProjectListPage, ProjectDetailPage } from './pages/Projects';
import { MyTasksPage, TeamTasksPage, AdminUsersPage, ReportsPage, NotificationsPage, ProfilePage } from './pages/Pages';
import { AIAssistantPage } from './pages/AIAssistant';
import { RFPListPage, RFPBuilderPage } from './pages/RFPBuilder';
import { GlobalProposalListPage, ProposalListPage, ProposalReviewPage } from './pages/ProposalEval';
import { RFPDashboardPage } from './pages/RFPDashboard';
import AuthCallback from './pages/AuthCallback';
import { rolesConfig } from './rolesConfig';
import './styles/global.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

// Root Route handler: redirects authenticated users to dashboard and unauthenticated to OAuth login
const RootRedirect = () => {
  const { user, loading, isAuthenticated } = useAuth();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

  if (loading) {
    return (
      <div className="page-loader" style={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
        <div className="spinner" />
        <p style={{ margin: 0, fontSize: '14px', color: 'var(--px-text-secondary)' }}>Verifying session…</p>
      </div>
    );
  }

  if (isAuthenticated && user) {
    return <Navigate to="/dashboard" replace />;
  }

  window.location.replace(`${backendUrl}/api/auth/login`);
  return null;
};

// Protected Route wrapper enforcing session & role permissions
const ProtectedRoute = ({ children, roles }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="page-loader" style={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
        <div className="spinner" />
        <p style={{ margin: 0, fontSize: '14px', color: 'var(--px-text-secondary)' }}>Verifying session…</p>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
    window.location.replace(`${backendUrl}/api/auth/login`);
    return null;
  }

  const effectiveRole = user?.role || user?.clientRole || 'Viewer';

  // Role check if route specifies explicit roles
  if (roles && !roles.includes(effectiveRole)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Allowed routes check against rolesConfig if configured
  const roleConfig = rolesConfig[effectiveRole];
  if (roleConfig?.allowedRoutes) {
    const isAllowed = roleConfig.allowedRoutes.some((routePattern) =>
      matchPath({ path: routePattern, end: false }, location.pathname)
    );
    if (!isAllowed) {
      return <Navigate to={roleConfig.landingRoute || '/dashboard'} replace />;
    }
  }

  return <AppLayout>{children}</AppLayout>;
};

function AppRoutes() {
  return (
    <Routes>
      {/* OAuth Callback (Never protect this route) */}
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Public / Login Routes (Auto-redirect to OAuth) */}
      <Route path="/login" element={<RootRedirect />} />

      {/* Protected Application Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/projects" element={<ProtectedRoute><ProjectListPage /></ProtectedRoute>} />
      <Route path="/projects/:id" element={<ProtectedRoute><ProjectDetailPage /></ProtectedRoute>} />
      <Route path="/my-tasks" element={<ProtectedRoute><MyTasksPage /></ProtectedRoute>} />
      <Route path="/team-tasks" element={<ProtectedRoute roles={['project_director', 'project_manager', 'site_engineer', 'client_rep', 'Admin']}><TeamTasksPage /></ProtectedRoute>} />
      <Route path="/ai-assistant" element={<ProtectedRoute><AIAssistantPage /></ProtectedRoute>} />
      <Route path="/rfps" element={<ProtectedRoute><RFPListPage /></ProtectedRoute>} />
      <Route path="/rfps/:id" element={<ProtectedRoute><RFPBuilderPage /></ProtectedRoute>} />
      <Route path="/rfps/:rfpId/proposals" element={<ProtectedRoute><ProposalListPage /></ProtectedRoute>} />
      <Route path="/proposals" element={<ProtectedRoute><GlobalProposalListPage /></ProtectedRoute>} />
      <Route path="/proposals/:id" element={<ProtectedRoute><ProposalReviewPage /></ProtectedRoute>} />
      <Route path="/rfp-dashboard" element={<ProtectedRoute><RFPDashboardPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute roles={['project_director', 'Admin']}><AdminUsersPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />

      {/* Root & Fallback Redirects */}
      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <Toaster
              position="top-right"
              toastOptions={{
                style: { fontFamily: 'var(--px-font)', fontSize: '0.88rem', borderRadius: '8px' },
                duration: 3000,
              }}
            />
            <AppRoutes />
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </Provider>
  );
}

export default App;
